import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { getIdToken } from "services/auth/auth";

interface UseFetchReturn<T> {
    data: T | null;
    loading: boolean;
    error: string;
    fetchData: (url: string, options?: RequestInit) => Promise<void>;
    setData: (data: T | null) => void;
    setError: (error: string) => void;
    setLoading: (loading: boolean) => void;
}

const useFetch = <T = unknown>(): UseFetchReturn<T> => {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const token = useSelector(getIdToken);
    const history = useHistory();

    const responseHandler = useCallback(
        (response: Response) => {
            if (response.status === 403 || response.status === 401) {
                history.replace("/");

                return;
            }

            if (!response.ok) {
                throw new Error(response.statusText);
            }

            return response.json();
        },
        [history]
    );

    const fetchData = useCallback(
        async (url: string, requestOptions: RequestInit = {}) => {
            setLoading(true);
            setError("");

            try {
                const defaultHeaders: Record<string, string> = token
                    ? { Authorization: `Bearer ${token}` }
                    : {};
                const headers = { ...defaultHeaders, ...requestOptions.headers };

                const response = await fetch(url, {
                    ...requestOptions,
                    headers,
                });

                const result = await responseHandler(response);
                setData(result);
            } catch (err) {
                setError(err instanceof Error ? err.message : "An error occurred");
            } finally {
                setLoading(false);
            }
        },
        [token, responseHandler]
    );

    return {
        data,
        loading,
        error,
        fetchData,
        setData,
        setError,
        setLoading,
    };
};

export default useFetch;
